import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { DataAPIContext, CREATE, GET_ONE, UPDATE } from '../api';
import DeleteButton from '../components/DeleteButton';
import FormActionButtons from '../components/FormActionButtons';
import PageMeta from '../components/PageMeta';
import useLeavePrompt, { LEAVE_WITHOUT_SAVING_MESSAGE } from '../components/useLeavePrompt';
import { ContextStore } from '../store/contextStore';
import { ensureOk, useIsWidthUp } from '../utils';

const WIZARD_STEPS = ['Basic Info', 'Contact', 'Admin User', 'Review'];

const generateCode = (name) =>
    name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

// The drawer and the wizard close by state, not by navigating, so X, CANCEL
// and a click outside ask here; useLeavePrompt covers leaving the page (CP-8411).
const confirmDiscard = (dirty) => !dirty || window.confirm(LEAVE_WITHOUT_SAVING_MESSAGE);

const toEditForm = (licensee) => ({
    name: licensee.name || '',
    code: licensee.code || '',
    url: licensee.url || '',
    email: licensee.email || '',
});

// ─── Edit Drawer ─────────────────────────────────────────────────────────────
const LicenseeEditDrawer = ({ open, licensee, onClose, onSaved }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState({ name: '', code: '', url: '', email: '' });
    const [initialForm, setInitialForm] = useState(form);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (licensee) {
            setForm(toEditForm(licensee));
            setInitialForm(toEditForm(licensee));
        }
    }, [licensee]);

    const dirty = open && !isEqual(form, initialForm);
    useLeavePrompt(dirty);

    const handleClose = () => {
        if (confirmDiscard(dirty)) onClose();
    };

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await dataAPI(UPDATE, 'tour_operator', {
                id: licensee.id,
                urlSuffix: 'update_licensee/',
                data: { name: form.name, url: form.url, email: form.email },
            });
            ensureOk(res, 'Error updating licensee');
            enqueueSnackbar('Licensee updated', { variant: 'success' });
            onSaved();
        } catch (err) {
            console.error('Error updating licensee', err);
            enqueueSnackbar(err?.message || 'Error updating licensee', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Edit Licensee</Typography>
                <IconButton onClick={handleClose} size="small" aria-label="close"><CloseIcon /></IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gap={2}>
                <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth variant="filled" size="small" />
                <TextField label="Code" name="code" value={form.code} fullWidth variant="filled" size="small" InputProps={{ readOnly: true }} />
                <TextField label="Website URL" name="url" value={form.url} onChange={handleChange} fullWidth variant="filled" size="small" />
                <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth variant="filled" size="small" />
            </Box>

            <FormActionButtons
                mode="edit"
                size="medium"
                sx={{ mt: 3 }}
                onCancel={handleClose}
                cancelDisabled={saving}
                onSubmit={handleSave}
                submitDisabled={saving}
                submitIcon={saving ? null : undefined}
                submitLabel={saving ? <CircularProgress size={20} /> : undefined}
            />
        </Drawer>
    );
};

// ─── Create Wizard ───────────────────────────────────────────────────────────
const INITIAL_WIZARD = {
    name: '',
    code: '',
    email: '',
    url: '',
    phone: '',
    logo: null,
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: '',
};

const LicenseeCreateWizard = ({ open, onClose, onCreated }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();

    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState({ ...INITIAL_WIZARD });
    const [validation, setValidation] = useState({ name_taken: false, code_taken: false });
    const [creating, setCreating] = useState(false);
    const validateTimer = useRef(null);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setForm({ ...INITIAL_WIZARD });
            setValidation({ name_taken: false, code_taken: false });
        }
    }, [open]);

    const dirty = open && !isEqual(form, INITIAL_WIZARD);
    useLeavePrompt(dirty);

    // A successful create closes through onClose directly, without asking.
    const handleClose = () => {
        if (confirmDiscard(dirty)) onClose();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'name') {
                next.code = generateCode(value);
            }
            return next;
        });
    };

    const validateNameCode = useCallback(async () => {
        if (!form.name && !form.code) return;
        try {
            const params = {};
            if (form.name) params.name = form.name;
            if (form.code) params.code = form.code;
            const qs = new URLSearchParams(params).toString();
            const res = await dataAPI(GET_ONE, 'tour_operator', {
                urlSuffix: `licensee_name_check/?${qs}`,
            });
            ensureOk(res, 'Validation check failed');
            setValidation({
                name_taken: !!res?.data?.name_taken,
                code_taken: !!res?.data?.code_taken,
            });
        } catch (err) {
            console.error('Validation check failed', err);
        }
    }, [dataAPI, form.name, form.code]);

    const handleBlurValidate = () => {
        clearTimeout(validateTimer.current);
        validateTimer.current = setTimeout(validateNameCode, 300);
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const canAdvance = () => {
        switch (activeStep) {
            case 0:
                return form.name.trim() !== '' && form.code.trim() !== '' && !validation.name_taken && !validation.code_taken;
            case 1:
                return true; // contact fields optional
            case 2:
                return form.admin_email.trim() !== '' && form.admin_password.trim() !== '';
            default:
                return true;
        }
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            const payload = {
                name: form.name,
                code: form.code,
                email: form.email,
                url: form.url,
                phone: form.phone,
                admin_email: form.admin_email,
                admin_password: form.admin_password,
                admin_first_name: form.admin_first_name,
                admin_last_name: form.admin_last_name,
            };
            const params = { urlSuffix: 'licensees/', data: payload };
            if (form.logo) {
                params.files = { logo: form.logo };
                params.upload = true;
            }
            const res = await dataAPI(CREATE, 'tour_operator', params);
            ensureOk(res, 'Error creating licensee');
            enqueueSnackbar('Licensee created successfully', { variant: 'success' });
            onCreated();
            onClose();
        } catch (err) {
            console.error('Error creating licensee', err);
            const msg = err?.body?.detail || err?.message || 'Error creating licensee';
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="Name" name="name" required
                            value={form.name} onChange={handleChange} onBlur={handleBlurValidate}
                            fullWidth variant="filled" size="small"
                            error={validation.name_taken}
                            helperText={validation.name_taken ? 'This name is already taken' : ''}
                        />
                        <TextField
                            label="Code" name="code" required
                            value={form.code} onChange={handleChange} onBlur={handleBlurValidate}
                            fullWidth variant="filled" size="small"
                            error={validation.code_taken}
                            helperText={validation.code_taken ? 'This code is already taken' : 'Auto-generated from name. You may edit it.'}
                        />
                    </Box>
                );
            case 1:
                return (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth variant="filled" size="small" helperText="Will be added as a support contact" />
                        <TextField label="Website URL" name="url" value={form.url} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <Box>
                            <Typography variant="body2" gutterBottom>Logo (optional, PNG/JPEG/GIF)</Typography>
                            <Button variant="outlined" component="label" size="small">
                                {form.logo ? form.logo.name : 'Upload Logo'}
                                <input type="file" hidden accept="image/png,image/jpeg,image/gif" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setForm((prev) => ({ ...prev, logo: file }));
                                }} />
                            </Button>
                            {form.logo && (
                                <DeleteButton size="small" sx={{ ml: 1 }} onClick={() => setForm((prev) => ({ ...prev, logo: null }))}>
                                    Delete
                                </DeleteButton>
                            )}
                            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                If not provided, the parent operator's logo will be used.
                            </Typography>
                        </Box>
                    </Box>
                );
            case 2:
                return (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField label="Admin Email" name="admin_email" required value={form.admin_email} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <TextField label="Admin Password" name="admin_password" required type="password" value={form.admin_password} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <TextField label="First Name" name="admin_first_name" value={form.admin_first_name} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <TextField label="Last Name" name="admin_last_name" value={form.admin_last_name} onChange={handleChange} fullWidth variant="filled" size="small" />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            This user will be created as the first full-access member for the new licensee.
                        </Typography>
                    </Box>
                );
            case 3:
                return (
                    <Box mt={1}>
                        <Typography variant="subtitle2" gutterBottom>Review details before creating:</Typography>
                        <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                            <li><Typography variant="body2"><strong>Name:</strong> {form.name}</Typography></li>
                            <li><Typography variant="body2"><strong>Code:</strong> {form.code}</Typography></li>
                            {form.email && <li><Typography variant="body2"><strong>Email:</strong> {form.email}</Typography></li>}
                            {form.phone && <li><Typography variant="body2"><strong>Phone:</strong> {form.phone}</Typography></li>}
                            {form.url && <li><Typography variant="body2"><strong>Website:</strong> {form.url}</Typography></li>}
                            <li><Typography variant="body2"><strong>Logo:</strong> {form.logo ? form.logo.name : 'Parent operator logo (default)'}</Typography></li>
                            <li><Typography variant="body2"><strong>Admin Email:</strong> {form.admin_email}</Typography></li>
                            {form.admin_first_name && <li><Typography variant="body2"><strong>Admin First Name:</strong> {form.admin_first_name}</Typography></li>}
                            {form.admin_last_name && <li><Typography variant="body2"><strong>Admin Last Name:</strong> {form.admin_last_name}</Typography></li>}
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Licensee</DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{ mb: 2, mt: 1 }}>
                    {WIZARD_STEPS.map((label) => (
                        <Step key={label}><StepLabel>{label}</StepLabel></Step>
                    ))}
                </Stepper>
                {renderStepContent()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={creating}>Cancel</Button>
                {activeStep > 0 && (
                    <Button onClick={handleBack} disabled={creating}>Back</Button>
                )}
                {activeStep < WIZARD_STEPS.length - 1 ? (
                    <Button variant="contained" onClick={handleNext} disabled={!canAdvance()}>
                        Next
                    </Button>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleCreate} disabled={creating}>
                        {creating ? <CircularProgress size={20} /> : 'Create Licensee'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const COLUMNS = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'code', headerName: 'Code', width: 140 },
    {
        field: 'url',
        headerName: 'Website',
        flex: 1,
        minWidth: 180,
        renderCell: (params) =>
            params.value ? (
                <a href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</a>
            ) : null,
    },
    {
        field: 'integrations',
        headerName: 'Integrations',
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => {
            const v = params?.row?.integrations || params?.value;
            return Array.isArray(v) ? v.join(', ') : (v || '');
        },
    },
    {
        field: 'created_ts',
        headerName: 'Created',
        width: 170,
        valueFormatter: (params) => {
            const v = params?.value;
            if (!v) return '';
            try {
                return new Date(v).toLocaleDateString();
            } catch {
                return v;
            }
        },
    },
];

const LicenseeManagement = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const { isFranchise } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');

    const [licensees, setLicensees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLicensee, setSelectedLicensee] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);

    const fetchLicensees = useCallback(async () => {
        setLoading(true);
        try {
            const res = ensureOk(await dataAPI(GET_ONE, 'tour_operator', { urlSuffix: 'licensees/' }), 'Failed to load licensees');
            const data = res?.data ?? res;
            setLicensees(Array.isArray(data) ? data : (data?.results ?? []));
        } catch (err) {
            console.error('Error fetching licensees', err);
            enqueueSnackbar(err?.message || 'Failed to load licensees', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [dataAPI, enqueueSnackbar]);

    useEffect(() => {
        if (isFranchise) fetchLicensees();
    }, [isFranchise, fetchLicensees]);

    const handleRowClick = (params) => {
        setSelectedLicensee(params.row);
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedLicensee(null);
    };

    const handleDrawerSaved = () => {
        handleDrawerClose();
        fetchLicensees();
    };

    const handleWizardCreated = () => {
        fetchLicensees();
    };

    // The menu hides this page from standalone operators, but the URL still opens it.
    if (!isFranchise) {
        return (
            <Box p={2}>
                <PageMeta title="Licensees" />
                <Typography variant="h6" mb={2}>Licensees</Typography>
                <Alert severity="info">
                    Licensees are available only for franchise operators. This operator is not set up as a franchise.
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <PageMeta title="Licensees" />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Licensees</Typography>
                {/* Icon-only on a phone, like the [+] on Activities (CP-5563). */}
                {isSmallScreen ? (
                    <Button variant="contained" color="primary" onClick={() => setWizardOpen(true)} aria-label="New Licensee">
                        <AddIcon />
                    </Button>
                ) : (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
                        New Licensee
                    </Button>
                )}
            </Box>

            <Box sx={{ height: 520, width: '100%' }}>
                <DataGridPro
                    rows={licensees}
                    columns={COLUMNS}
                    loading={loading}
                    density="compact"
                    disableSelectionOnClick
                    onRowClick={handleRowClick}
                    getRowId={(row) => row.id}
                    pagination
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                    }}
                />
            </Box>

            {/* Edit Drawer */}
            <LicenseeEditDrawer
                open={drawerOpen}
                licensee={selectedLicensee}
                onClose={handleDrawerClose}
                onSaved={handleDrawerSaved}
            />

            {/* Create Wizard Dialog */}
            <LicenseeCreateWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onCreated={handleWizardCreated}
            />
        </Box>
    );
};

export default LicenseeManagement;
